import { create } from "zustand";
import type { IdTokenClaims } from "@azure/msal-browser";
import {extractLabelFromPipe} from "@commons/Utils";

export type MsalIdTokenClaims = IdTokenClaims & {
    country?: string;
    "signInNames.emailAddress"?: string;
    accountEmail?: string;
    given_name?: string;
    family_name?: string;
    mobile?: string;
    city?: string;
    state?: string;
    streetAddress?: string;
    postalCode?: string;
    extension_consumerOrProfessional?: string;
    extension_ADB2C_CompanyName?: string;
    extension_ADB2C_VAT?: string;
    extension_ADB2C_CompanyAddress?: string;
    extension_ADB2C_CompanyZIP?: string;
    extension_ADB2C_CompanyCity?: string;
    extension_ADB2C_CompanyCountry?: string;
    extension_ADB2C_IDCompanyCountry?: string;
    extension_ADB2C_CompanyState?: string;
    extension_ADB2C_IDCompanyState?: string;
    extension_ADB2C_IDCountry?: string;
    extension_ADB2C_IDState?: string;
    extension_ADB2C_FromPolicy?: string;
    accountEnabled?: boolean;
};

export interface AuthState {
    loading: boolean;
    overlayVisible: boolean;
    overlayMessage?: string | null;
    setOverlayMessage: (message: string | null) => void;

    isLoggingOut: boolean;
    isAuthenticated: boolean;
    account?: any | null;
    idTokenClaims?: MsalIdTokenClaims | null;
    accessToken?: string | null;

    derivedCountry?: string | null;
    postLoginRedirectPath?: string | null;

    setAuth: (data: Partial<AuthState>) => void;
    setOverlay: (visible: boolean) => void;
    setPostLoginRedirect: (path: string | null) => void;
    reset: () => void;
}

function deriveCountryFromClaims(claims: any): string | null {
    if (!claims || !claims.country) return null;

    const result = extractLabelFromPipe(claims.country);

    return result ? result.toUpperCase() : null;
}

export const useAuthStore = create<AuthState>()((set) => ({
    loading: true,
    overlayVisible: false,
    overlayMessage: null,
    isLoggingOut: false,
    isAuthenticated: false,
    account: null,
    idTokenClaims: null,
    accessToken: null,
    derivedCountry: null,
    postLoginRedirectPath: null,

    setAuth: (data) =>
        set((prev) => {
            const next: AuthState = { ...prev, ...data };

            if (data.idTokenClaims !== undefined) {
                const incoming = data.idTokenClaims;
                const isEmptyObject =
                    incoming && typeof incoming === "object" && Object.keys(incoming).length === 0;

                const effectiveClaims = !incoming || isEmptyObject ? prev.idTokenClaims : incoming;

                next.idTokenClaims = (effectiveClaims ?? null) as any;
                next.derivedCountry = deriveCountryFromClaims(effectiveClaims);
            }

            return next;
        }),

    setOverlay: (visible) => set({ overlayVisible: visible }),
    setOverlayMessage: (message) => set({ overlayMessage: message }),

    setPostLoginRedirect: (path) => {
        const prev = sessionStorage.getItem("auth:returnUrl");
        if (prev === (path ?? null)) return;
        if (path) sessionStorage.setItem("auth:returnUrl", path);
        else sessionStorage.removeItem("auth:returnUrl");
        set({ postLoginRedirectPath: path });
    },

    reset: () =>
        set({
            loading: false,
            overlayVisible: false,
            overlayMessage: null,
            isLoggingOut: false,
            isAuthenticated: false,
            account: null,
            idTokenClaims: null,
            accessToken: null,
            derivedCountry: null,
            postLoginRedirectPath: null,
        }),
}));
